
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Headphones,
  LockKeyhole,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  Truck,
} from "lucide-react";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import "../../styles/cart.css";
import bg from "../../assets/bg.png";

/* =========================================================
   CONFIG
   ========================================================= */

const CART_KEY = "cart";
const AUTH_KEY = "auth";
const LOGIN_REDIRECT_KEY = "redirectAfterLogin";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://api.debutscrop.com/api";

/* =========================================================
   HELPERS
   ========================================================= */

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const getQuantity = (item) =>
  Math.max(
    1,
    Number(item?.qty ?? item?.quantity ?? 1)
  );

const getPrice = (item) =>
  Number(
    item?.price ??
      item?.sale_price ??
      item?.final_price ??
      item?.unit_price ??
      0
  ) || 0;

const getProductId = (item) =>
  item?.product_id ??
  item?.product?.id ??
  item?.id;

const getApiOrigin = () => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return "";
  }
};

const getProductImage = (item) => {
  const image =
    item?.image ||
    item?.image_url ||
    item?.photo ||
    item?.thumbnail ||
    item?.product?.image ||
    item?.product?.image_url ||
    "";

  if (!image) {
    return "/placeholder.png";
  }

  if (/^https?:\/\//i.test(image)) {
    return image;
  }

  if (image.startsWith("//")) {
    return `https:${image}`;
  }

  const origin = getApiOrigin();

  if (image.startsWith("/")) {
    return `${origin}${image}`;
  }

  return `${origin}/${image.replace(/^\/+/, "")}`;
};

const readCart = () => {
  try {
    const saved = JSON.parse(
      localStorage.getItem(CART_KEY) || "[]"
    );

    if (!Array.isArray(saved)) {
      return [];
    }

    return saved
      .map((item) => {
        const quantity = getQuantity(item);

        return {
          ...item,
          qty: quantity,
          quantity,
        };
      })
      .filter((item) => getQuantity(item) > 0);
  } catch (error) {
    console.error(
      "Error leyendo el carrito:",
      error
    );

    return [];
  }
};

const readAuth = () => {
  try {
    return JSON.parse(
      localStorage.getItem(AUTH_KEY) || "null"
    );
  } catch {
    return null;
  }
};

const isAuthenticated = () => {
  const auth = readAuth();
  return Boolean(auth?.access);
};

/* =========================================================
   COMPONENT
   ========================================================= */

export default function CartPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] =
    useState(false);

  /* =======================================================
     LOAD
     ======================================================= */

  const loadCart = useCallback(() => {
    setItems(readCart());
    setAuthenticated(isAuthenticated());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCart();

    const handleCartUpdate = () => {
      loadCart();
    };

    const handleStorage = () => {
      loadCart();
    };

    const handleAuthUpdate = () => {
      loadCart();
    };

    window.addEventListener(
      "cartUpdated",
      handleCartUpdate
    );

    window.addEventListener(
      "storage",
      handleStorage
    );

    window.addEventListener(
      "authUpdated",
      handleAuthUpdate
    );

    return () => {
      window.removeEventListener(
        "cartUpdated",
        handleCartUpdate
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );

      window.removeEventListener(
        "authUpdated",
        handleAuthUpdate
      );
    };
  }, [loadCart]);

  /* =======================================================
     SAVE CART
     ======================================================= */

  const saveCart = useCallback((nextItems) => {
    const normalized = nextItems.map((item) => {
      const nextQuantity = getQuantity(item);

      return {
        ...item,
        qty: nextQuantity,
        quantity: nextQuantity,
      };
    });

    localStorage.setItem(
      CART_KEY,
      JSON.stringify(normalized)
    );

    setItems(normalized);

    window.dispatchEvent(
      new Event("cartUpdated")
    );
  }, []);

  /* =======================================================
     QUANTITY
     ======================================================= */

  const changeQuantity = (id, amount) => {
    const nextItems = items.map((item) => {
      if (
        String(getProductId(item)) !==
        String(id)
      ) {
        return item;
      }

      let nextQuantity =
        getQuantity(item) + amount;

      nextQuantity = Math.max(
        1,
        nextQuantity
      );

      const stock = Number(
        item?.stock ??
          item?.product?.stock
      );

      if (
        Number.isFinite(stock) &&
        stock > 0
      ) {
        nextQuantity = Math.min(
          nextQuantity,
          stock
        );
      }

      return {
        ...item,
        qty: nextQuantity,
        quantity: nextQuantity,
      };
    });

    saveCart(nextItems);
  };

  /* =======================================================
     REMOVE
     ======================================================= */

  const removeItem = (id) => {
    const nextItems = items.filter(
      (item) =>
        String(getProductId(item)) !==
        String(id)
    );

    saveCart(nextItems);
  };

  /* =======================================================
     CLEAR
     ======================================================= */

  const clearCart = () => {
    saveCart([]);
  };

  /* =======================================================
     TOTALS
     ======================================================= */

  const totalItems = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total + getQuantity(item),
        0
      ),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total +
          getPrice(item) *
            getQuantity(item),
        0
      ),
    [items]
  );

  /* =======================================================
     CHECKOUT
     ======================================================= */

  const handleCheckout = () => {
    if (!items.length) {
      return;
    }

    const auth = readAuth();
    const loggedIn = Boolean(
      auth?.access
    );

    setAuthenticated(loggedIn);

    if (!loggedIn) {
      localStorage.setItem(
        LOGIN_REDIRECT_KEY,
        "/checkout"
      );

      navigate("/login", {
        state: {
          from: "/checkout",
          message:
            "Inicia sesión o crea una cuenta para finalizar tu compra.",
        },
      });

      return;
    }

    navigate("/checkout");
  };

  /* =======================================================
     PRODUCT
     ======================================================= */

  const handleProductClick = (id) => {
    if (!id) {
      return;
    }

    navigate(`/product/${id}`);
  };

  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <div
        className="cart-page cart-page-loading"
        style={{
          "--cart-bg": `url(${bg})`,
        }}
      >
        <Navbar />

        <main className="cart-loading">
          <div className="cart-loader-ring" />

          <p>
            Cargando carrito...
          </p>
        </main>

        <Footer />
      </div>
    );
  }

  /* =======================================================
     PAGE
     ======================================================= */

  return (
    <div
      className="cart-page"
      style={{
        "--cart-bg": `url(${bg})`,
      }}
    >
      <Navbar />

      {/* ===================================================
          HERO
      =================================================== */}

      <section className="cart-hero">
        <div className="cart-hero-overlay" />

        <div className="cart-container cart-hero-content">
          <div className="cart-hero-copy">
            <span className="cart-hero-kicker">
              DEBUTS CROP · E-COMMERCE
            </span>

            <h1>
              TU CARRITO
              <strong>
                DE COMPRAS
              </strong>
            </h1>

            <p>
              Revisa tus productos antes
              de finalizar tu compra.
            </p>
          </div>

          <div
            className="cart-hero-icon"
            aria-hidden="true"
          >
            <ShoppingCart
              size={88}
              strokeWidth={1.25}
            />
          </div>
        </div>
      </section>

      {/* ===================================================
          BREADCRUMB
      =================================================== */}

      <div className="cart-container">
        <nav
          className="cart-breadcrumb"
          aria-label="Navegación"
        >
          <button
            type="button"
            onClick={() =>
              navigate("/")
            }
          >
            Inicio
          </button>

          <ChevronRight size={15} />

          <button
            type="button"
            onClick={() =>
              navigate("/catalog")
            }
          >
            Catálogo
          </button>

          <ChevronRight size={15} />

          <span>
            Carrito
          </span>
        </nav>
      </div>

      {/* ===================================================
          EMPTY
      =================================================== */}

      {!items.length ? (
        <main className="cart-container cart-empty">
          <div className="cart-empty-card">
            <div className="cart-empty-icon">
              <ShoppingCart size={52} />
            </div>

            <span className="cart-empty-kicker">
              TU CARRITO ESTÁ VACÍO
            </span>

            <h2>
              ¿LISTO PARA ELEGIR?
            </h2>

            <p>
              Explora nuestro catálogo y
              agrega los productos que quieras.
              No necesitas iniciar sesión para
              comenzar tu compra.
            </p>

            <button
              type="button"
              className="cart-primary-button"
              onClick={() =>
                navigate("/catalog")
              }
            >
              VER CATÁLOGO
              <ArrowRight size={18} />
            </button>
          </div>
        </main>
      ) : (
        <>
          {/* =================================================
              CONTENT
          ================================================= */}

          <main className="cart-container cart-content">
            <div className="cart-layout">

              {/* =============================================
                  PRODUCTS
              ============================================= */}

              <section className="cart-products-card">
                <header className="cart-section-header">
                  <div>
                    <span className="cart-section-kicker">
                      TU SELECCIÓN
                    </span>

                    <h2>
                      MI CARRITO{" "}
                      <small>
                        ({totalItems})
                      </small>
                    </h2>
                  </div>

                  <button
                    type="button"
                    className="cart-continue-button"
                    onClick={() =>
                      navigate("/catalog")
                    }
                  >
                    Seguir comprando
                    <ArrowRight size={17} />
                  </button>
                </header>

                <div className="cart-table-head">
                  <span>
                    Producto
                  </span>

                  <span>
                    Precio
                  </span>

                  <span>
                    Cantidad
                  </span>

                  <span>
                    Total
                  </span>

                  <span aria-hidden="true" />
                </div>

                <div className="cart-items">
                  {items.map((item) => {
                    const itemPrice =
                      getPrice(item);

                    const itemQuantity =
                      getQuantity(item);

                    const itemTotal =
                      itemPrice *
                      itemQuantity;

                    const image =
                      getProductImage(item);

                    const category =
                      item?.category?.name ||
                      item?.category ||
                      item?.category_name ||
                      "Producto";

                    return (
                      <article
                        className="cart-item"
                        key={String(
                          getProductId(item)
                        )}
                      >
                        <div className="cart-product-info">
                          <button
                            type="button"
                            className="cart-product-image"
                            onClick={() =>
                              handleProductClick(
                                getProductId(item)
                              )
                            }
                            aria-label={`Ver ${
                              item.name ||
                              "producto"
                            }`}
                          >
                            <img
                              src={image}
                              alt={
                                item.name ||
                                "Producto Debuts Crop"
                              }
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/placeholder.png";
                              }}
                            />
                          </button>

                          <div className="cart-product-text">
                            <span className="cart-product-category">
                              {category}
                            </span>

                            <h3>
                              {item.name ||
                                item?.product?.name ||
                                "Producto Debuts Crop"}
                            </h3>
                          </div>
                        </div>

                        <div className="cart-item-price">
                          {money(itemPrice)}
                        </div>

                        <div className="cart-quantity">
                          <button
                            type="button"
                            onClick={() =>
                              changeQuantity(
                                getProductId(item),
                                -1
                              )
                            }
                            aria-label="Disminuir cantidad"
                            disabled={
                              itemQuantity <= 1
                            }
                          >
                            <Minus size={15} />
                          </button>

                          <strong>
                            {itemQuantity}
                          </strong>

                          <button
                            type="button"
                            onClick={() =>
                              changeQuantity(
                                getProductId(item),
                                1
                              )
                            }
                            aria-label="Aumentar cantidad"
                            disabled={
                              Number(
                                item?.stock ??
                                  item?.product?.stock
                              ) > 0 &&
                              itemQuantity >=
                                Number(
                                  item?.stock ??
                                    item?.product?.stock
                                )
                            }
                          >
                            <Plus size={15} />
                          </button>
                        </div>

                        <div className="cart-item-total">
                          {money(itemTotal)}
                        </div>

                        <button
                          type="button"
                          className="cart-remove-button"
                          onClick={() =>
                            removeItem(
                              getProductId(item)
                            )
                          }
                          aria-label={`Eliminar ${
                            item.name ||
                            "producto"
                          }`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </article>
                    );
                  })}
                </div>

                <footer className="cart-products-footer">
                  <button
                    type="button"
                    className="cart-clear-button"
                    onClick={clearCart}
                  >
                    <Trash2 size={16} />
                    Vaciar carrito
                  </button>

                  <span>
                    {totalItems}{" "}
                    {totalItems === 1
                      ? "producto"
                      : "productos"}{" "}
                    seleccionados
                  </span>
                </footer>
              </section>

              {/* =============================================
                  SUMMARY
              ============================================= */}

              <aside className="cart-summary-card">
                <div className="cart-summary-header">
                  <div className="cart-summary-icon">
                    <CreditCard size={21} />
                  </div>

                  <h2>
                    RESUMEN DE COMPRA
                  </h2>
                </div>

                <div className="cart-summary-lines">
                  <div>
                    <span>
                      Subtotal (
                      {totalItems}{" "}
                      {totalItems === 1
                        ? "producto"
                        : "productos"}
                      )
                    </span>

                    <strong>
                      {money(subtotal)}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Envío
                    </span>

                    <strong className="cart-summary-muted">
                      Calculado en checkout
                    </strong>
                  </div>
                </div>

                <div className="cart-summary-total">
                  <span>
                    Total
                  </span>

                  <strong>
                    {money(subtotal)}
                  </strong>
                </div>

                <button
                  type="button"
                  className="cart-checkout-button"
                  onClick={handleCheckout}
                >
                  <LockKeyhole size={18} />

                  {authenticated
                    ? "FINALIZAR COMPRA"
                    : "INICIAR SESIÓN PARA COMPRAR"}
                </button>

                <p className="cart-login-note">
                  {authenticated
                    ? "Tu cuenta está lista para continuar con el checkout."
                    : "Puedes seleccionar productos y llenar tu carrito sin iniciar sesión. Para finalizar la compra deberás iniciar sesión o crear una cuenta."}
                </p>

                <div className="cart-secure-box">
                  <div className="cart-secure-title">
                    <ShieldCheck size={24} />

                    <div>
                      <strong>
                        Compra segura
                      </strong>

                      <span>
                        Tus datos se mantienen
                        protegidos.
                      </span>
                    </div>
                  </div>

                  <div className="cart-payment-icons">
                    <span>
                      VISA
                    </span>

                    <span>
                      MC
                    </span>

                    <span>
                      AMEX
                    </span>

                    <span>
                      PSE
                    </span>

                    <span>
                      NEQUI
                    </span>
                  </div>
                </div>
              </aside>
            </div>
          </main>

          {/* =================================================
              BENEFITS
          ================================================= */}

          <section className="cart-benefits">
            <div className="cart-container cart-benefits-grid">
              <div className="cart-benefit">
                <Truck size={28} />

                <div>
                  <strong>
                    Envíos a todo el país
                  </strong>

                  <span>
                    Consulta cobertura y
                    tiempos.
                  </span>
                </div>
              </div>

              <div className="cart-benefit">
                <CheckCircle2 size={28} />

                <div>
                  <strong>
                    Productos originales
                  </strong>

                  <span>
                    Calidad y respaldo
                    Debuts Crop.
                  </span>
                </div>
              </div>

              <div className="cart-benefit">
                <CreditCard size={28} />

                <div>
                  <strong>
                    Pagos confiables
                  </strong>

                  <span>
                    Proceso protegido en
                    checkout.
                  </span>
                </div>
              </div>

              <div className="cart-benefit">
                <Headphones size={28} />

                <div>
                  <strong>
                    Atención personalizada
                  </strong>

                  <span>
                    Estamos para ayudarte.
                  </span>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
